import {track} from "@amplitude/analytics-browser";
import {useLazyQuery, useQuery, useSubscription} from "@apollo/client";
import {
  faBan,
  faExclamationTriangle,
  faFilter,
  faFilterCircleXmark,
  faLayerGroup,
  faLocationCrosshairs,
  faLocationDot,
  faMagnifyingGlassPlus,
} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Alert, Button, Drawer, Group, Menu, Space, Text} from "@mantine/core";
import {compact, debounce, isEmpty, noop, omit} from "lodash";
import React, {useEffect, useMemo, useReducer} from "react";
import NoSSR from "react-no-ssr";
import {v4 as getNS, v5 as uuidNameSpace} from "uuid";

import FilterPanel, {areFiltersApplied} from "./FilterPanel/FilterPanel";
import classes from "./Search.module.scss";
import SearchLoader from "./SearchLoader/SearchLoader";
import {DEFAULT_LOCATION_ZOOM} from "../../constants";
import * as queries from "../../graphql/queries";
import * as subscriptions from "../../graphql/subscriptions";
import type {
  Location,
  MapCluster,
  MapSearchInput,
  SearchResult,
} from "../../graphql/types";
import {getLogger} from "../../lib/logging";
import {useOnlineStatus} from "../../lib/onlineStatus";
import {DEFAULT_SEARCH_STATE} from "../../lib/search";
import type {HoverEvent, SearchState, SelectionEvent} from "../../lib/types";
import type {SearchPopoverProps} from "../../lib/types";
import {usePosition} from "../contexts/PositionContext";
import HelpLink from "../HelpLink";
import LayerPanel from "../LayerPanel";
import MapView from "../MapView/MapView";
import PlacePicker from "../PlacePicker";
import FacilityPopover from "../popovers/FacilityPopover";
import ParkPopover from "../popovers/ParkPopover";
import UnitPopover from "../popovers/UnitPopover";
import Tutorial from "../Tutorial";

const log = getLogger(__filename);
const RequestNameSpace = getNS();

// Milliseconds to delay before showing the search loader
const SEARCH_LOADER_DELAY_MS = 1000;

type SearchAction =
  | {type: "UPDATE_SEARCH_STATE"; state: Partial<SearchState>}
  | {type: "UPDATE_SEARCH_INPUT"; searchInput: Partial<MapSearchInput>}
  | {type: "SET_MAP_CENTER" | "SET_OWN_CENTER"; location: Location | null}
  | {type: "SET_ZOOM"; zoom: number}
  | {type: "SET_LAYERS"; layers: string[]}
  | {type: "SET_RESULTS"; results: SearchResult[]}
  | {
      type:
        | "SET_SHOW_SEARCH_LOADER"
        | "SET_SHOW_FILTERS"
        | "SET_SHOW_LAYERS"
        | "SET_SHOW_RESULTS_STRIP"
        | "SET_RUN_TUTORIAL";
      value: boolean;
    }
  | {type: "SET_SELECTION"; selection: SelectionEvent | null}
  | {type: "SET_HOVER"; hover: HoverEvent | null};

function searchStateReducer(
  state: SearchState,
  action: SearchAction
): SearchState {
  const version = {
    version: state.version + 1,
  };

  switch (action.type) {
    case "UPDATE_SEARCH_STATE":
      return {
        ...state,
        ...version,
        ...action.state,
      };
    case "UPDATE_SEARCH_INPUT":
      return {
        ...state,
        ...version,
        searchInput: {
          ...state.searchInput,
          ...action.searchInput,
          filter: {
            ...(state.searchInput.filter || {}),
            ...(action.searchInput.filter || {}),
          },
        },
      };
    case "SET_MAP_CENTER":
      return {
        ...state,
        ...version,
        mapCenter: action.location || state.mapCenter,
      };
    case "SET_OWN_CENTER":
      return {
        ...state,
        ...version,
        ownCenter: action.location || state.ownCenter,
      };
    case "SET_ZOOM":
      return {
        ...state,
        ...version,
        searchInput: {
          ...state.searchInput,
          zoom: action.zoom || state.searchInput.zoom,
        },
      };
    case "SET_LAYERS":
      return {
        ...state,
        ...version,
        layers: action.layers,
      };
    case "SET_RESULTS":
      return {
        ...state,
        results: action.results,
      };
    case "SET_SHOW_SEARCH_LOADER":
      return {
        ...state,
        ...version,
        showSearchLoader: action.value,
      };
    case "SET_SHOW_FILTERS":
      return {
        ...state,
        ...version,
        showFilters: action.value,
      };
    case "SET_SHOW_LAYERS":
      return {
        ...state,
        ...version,
        showLayers: action.value,
      };
    case "SET_SHOW_RESULTS_STRIP":
      return {
        ...state,
        ...version,
        showResultsStrip: action.value,
      };
    case "SET_RUN_TUTORIAL":
      return {
        ...state,
        ...version,
        runTutorial: action.value,
      };
    case "SET_SELECTION":
      return {
        ...state,
        ...version,
        selection: action.selection,
      };
    case "SET_HOVER":
      return {
        ...state,
        // ...version,
        hover: action.hover,
      };
    default:
      throw new Error("Unknown action type");
  }
}

export interface SearchProps {
  initialState?: SearchState;
  onStateChange?(searchState: SearchState): void;
}

export default function Search({
  initialState = DEFAULT_SEARCH_STATE,
  onStateChange = noop,
}: SearchProps) {
  const isOnline = useOnlineStatus();

  const {data: {user: {location: locationResult = {}} = {}} = {}} = useQuery(
    queries.GET_LOCATION
  );

  const [searchState, dispatch] = useReducer(searchStateReducer, initialState);
  const {
    searchInput,
    results,
    layers,
    showFilters,
    showLayers,
    showSearchLoader,
    hover,
    selection,
    mapRect,
    mapCenter,
    ownCenter,
    runTutorial,
  } = searchState;

  // Propagate state changes to parent component
  useEffect(() => {
    onStateChange(searchState);
  }, [searchState]);

  // Search results
  const [
    mapSearch,
    {
      loading: searchLoading,
      error: searchError,
      data: {search: {mapSearch: searchResult = {}} = {}} = {},
    },
  ] = useLazyQuery(queries.MAP_SEARCH);

  const position = usePosition();

  // Handle search results
  useEffect(() => {
    if (!searchLoading && !searchError && searchResult.results) {
      log.info(`Search returned ${searchResult.results.length} results`);
      dispatch({type: "SET_RESULTS", results: searchResult.results});
      dispatch({type: "SET_RUN_TUTORIAL", value: true});
    }
  }, [searchLoading, searchError, searchResult]);

  // Show the search loader on a delay
  useEffect(() => {
    if (!searchLoading) {
      dispatch({type: "SET_SHOW_SEARCH_LOADER", value: false});
    }

    const timeout = setTimeout(
      () =>
        dispatch({
          type: "SET_SHOW_SEARCH_LOADER",
          value: searchLoading && !position.watching,
        }),
      SEARCH_LOADER_DELAY_MS
    );
    return () => clearTimeout(timeout);
  }, [searchLoading, position.watching]);

  const debouncedMapSearch = useMemo(() => {
    return debounce(args => {
      log.info(`Searching`, {searchState: omit(searchState, "results")});
      const searchArgs = {
        ...args,
        context: {
          ...(args.context || {}),
          requestTrackerId: uuidNameSpace("SEARCH", RequestNameSpace),
        },
      };

      return mapSearch(searchArgs);
    }, 600);
  }, []);

  const refreshSearch = () => {
    if (
      !searchInput.filter?.bounds ||
      !searchInput.filter?.bounds?.xmax ||
      !searchInput.filter?.bounds?.xmin ||
      !searchInput.filter?.bounds?.ymax ||
      !searchInput.filter?.bounds?.ymin
    ) {
      // Can't search without bounds
      return;
    }

    debouncedMapSearch({
      variables: {
        filter: searchInput.filter,
        zoom: searchInput.zoom,
        onlyMatches: searchInput.onlyMatches,
        cluster: true,
        page: 1,
        pageSize: 500,
        facetSize: 100,
        includeBlmBoundaries: layers.includes("blm"),
        includeUsfsBoundaries: layers.includes("usfs"),
      },
    });
  };

  // Search when filters changed
  useEffect(() => {
    // log.debug("Filters changed", {searchInput: searchInput});

    refreshSearch();
  }, [searchInput, layers]);

  // Subscribe to updates for search results
  const {error: _updatesError, data: {updates: update = undefined} = {}} =
    useSubscription(subscriptions.UPDATES, {
      variables: {
        inputs: results
          .filter(result => result.type && result.id)
          .map(result => ({
            modelClass: result.type,
            modelId: result.id,
          })),
      },
    });
  useEffect(() => {
    const model = update?.model;
    if (!model) {
      return;
    }

    log.debug(`Model updated: ${JSON.stringify(update)}`);
    refreshSearch();
  }, [update]);

  // Initially center the map on user's detected position
  useEffect(() => {
    if (mapCenter) {
      return;
    }

    let userLocation: Location | null;
    if (searchState.mapCenter) {
      // Get location from previous search state
      log.debug("Got user location from search state");
      userLocation = searchState.mapCenter;
      dispatch({type: "SET_MAP_CENTER", location: userLocation});
    } else if (position.coordinates) {
      // Get location from user's detected geolocation
      log.debug("Got user location from geolocation API");
      userLocation = {
        latitude: position.coordinates.latitude,
        longitude: position.coordinates.longitude,
        accuracyMeters: position.coordinates.accuracy,
        __typename: "Location",
      };
      dispatch({type: "SET_OWN_CENTER", location: userLocation});
    } else if (locationResult) {
      // Get location from user's IP address
      log.debug("Got user location from IP address");
      userLocation = locationResult;
      dispatch({type: "SET_OWN_CENTER", location: userLocation});
    }
  }, [mapCenter, searchState, locationResult]);

  // Re-center the map when the user's tracked position changes
  useEffect(() => {
    if (position.updating || !position.coordinates) {
      return;
    }

    dispatch({
      type: "SET_OWN_CENTER",
      location: {
        latitude: position.coordinates.latitude,
        longitude: position.coordinates.longitude,
        accuracyMeters: position.coordinates.accuracy,
        __typename: "Location",
      },
    });
  }, [position.coordinates, position.updating]);

  // Update the map marker for the user's own position
  useEffect(() => {
    if (ownCenter) {
      dispatch({type: "SET_MAP_CENTER", location: ownCenter});
    }
  }, [ownCenter]);

  useEffect(() => {
    track("Search Page Viewed");
  }, []);

  const popoverProps: Partial<SearchPopoverProps> = {
    searchInput,
  };

  const popoverContainerStyle = useMemo(() => {
    const style: any = {
      top: 0,
      left: 0,
    };

    // Show overlay at clicked location
    if (selection && selection.pixel && mapRect) {
      let [x, y] = selection.pixel;

      // Recalculate if pixel is too close to right or bottom boundary
      // const overlayRect = overlay.getBoundingClientRect();

      const overlayMaxWidth = 610; // overlayRect.width;
      if (x + overlayMaxWidth > mapRect.right) {
        x = mapRect.right - overlayMaxWidth;
      }

      const overlayMaxHeight = 555;
      if (y + overlayMaxHeight > mapRect.bottom) {
        y = mapRect.bottom - overlayMaxHeight;
      }

      style.top = y;
      style.left = x;
    }

    return style;
  }, [selection, mapRect]);

  const hoverContainerStyle = useMemo(() => {
    const style: any = {
      top: 0,
      left: 0,
    };

    // Show tooltip at hovered location
    if (hover && hover.pixel && mapRect) {
      let [x, y] = hover.pixel;

      // Recalculate if pixel is too close to right or bottom boundary
      // const overlayRect = overlay.getBoundingClientRect();

      const overlayMaxWidth = 200; // overlayRect.width;
      if (x + overlayMaxWidth > mapRect.right) {
        x = mapRect.right - overlayMaxWidth;
      }

      const overlayMaxHeight = 100;
      if (y + overlayMaxHeight > mapRect.bottom) {
        y = mapRect.bottom - overlayMaxHeight;
      }

      style.top = y;
      style.left = x;
    }

    return style;
  }, [hover, mapRect]);

  let truncatedResults = false;
  const pagination = searchResult.pagination;
  if (pagination) {
    truncatedResults = pagination.totalResults > pagination.pageSize;
  }

  const filtersApplied = useMemo(
    () => areFiltersApplied(searchInput),
    [searchInput]
  );

  return (
    <div className={classes.search}>
      <div className={classes.buttonBar}>
        <Group>
          <PlacePicker
            className={classes.placePicker}
            size="sm"
            location={mapCenter || ownCenter}
            onSelection={result => {
              if (!result.location) {
                log.warn(
                  `Unable to center map because selected ${result.type} result ${result.id} has no location`
                );
                return;
              }
              dispatch({type: "SET_MAP_CENTER", location: result.location});
              dispatch({type: "SET_ZOOM", zoom: DEFAULT_LOCATION_ZOOM}); // TODO: Get min zoom level from place
            }}
          />
          <Button.Group>
            {/*!searchState.showResultsStrip && (
              <Button
                size="sm"
                variant="default"
                leftSection={<FontAwesomeIcon icon={faList} />}
                onClick={() =>
                  dispatch({type: "SET_SHOW_RESULTS_STRIP", value: true})
                }>
                Results
              </Button>
            )*/}
            <Button
              className={classes.filterButton}
              classNames={{label: classes.filterButtonLabel}}
              onClick={() =>
                dispatch({type: "SET_SHOW_FILTERS", value: !showFilters})
              }
              title="Show filter options"
              size="sm"
              variant={filtersApplied ? "primary" : "default"}
              leftSection={
                <FontAwesomeIcon
                  icon={filtersApplied ? faFilterCircleXmark : faFilter}
                />
              }>
              Filter
            </Button>
            <Button
              className={classes.layersButton}
              classNames={{label: classes.layersButtonLabel}}
              onClick={() =>
                dispatch({type: "SET_SHOW_LAYERS", value: !showLayers})
              }
              title="Show map layers"
              size="sm"
              variant="default"
              leftSection={<FontAwesomeIcon icon={faLayerGroup} />}>
              Layers
            </Button>
            <NoSSR>
              <Menu>
                <Menu.Target>
                  <Button
                    className={classes.locationButton}
                    title="Location"
                    size="sm"
                    loading={!position.watching && position.updating}
                    variant={position.watching ? "primary" : "default"}>
                    <FontAwesomeIcon
                      icon={
                        position.watching ? faLocationCrosshairs : faLocationDot
                      }
                      beat={position.watching || position.updating}
                    />
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    disabled={position.watching || position.updating}
                    leftSection={<FontAwesomeIcon icon={faLocationDot} />}
                    onClick={event => {
                      event.preventDefault();
                      position.updatePosition();
                    }}>
                    Center on my location
                  </Menu.Item>
                  <Menu.Item
                    leftSection={
                      <FontAwesomeIcon icon={faLocationCrosshairs} />
                    }
                    onClick={event => {
                      event.preventDefault();
                      position.watchPosition(!position.watching);
                    }}>
                    {!position.watching
                      ? "Live track location"
                      : "Stop live tracking location"}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </NoSSR>
          </Button.Group>
        </Group>
      </div>

      <div className={classes.results}>
        {/*searchState.showResultsStrip && (
          <ResultsStrip
            results={results || []}
            limit={50}
            onClose={() => setSearchState({...searchState, showResultsStrip: false}}
            onSelection={({result, pixel}) => {
              const [x, y] = pixel ? pixel : [0, 0];
              setSelection({
                result,
                pixel: [x, y - 55], // Compensate for offset of search results element screen position
              });
            }}
          />
        )*/}

        <MapView
          className={classes.mapView}
          self={ownCenter}
          center={mapCenter}
          zoom={searchInput?.zoom}
          resultList={[...results]}
          satelliteView={true}
          enabledLayers={compact([
            searchState.layers.includes("snow") ? "snow" : null,
            searchState.layers.includes("fflood") ? "fflood" : null,
          ])}
          onBoundsChanged={(
            lowerLeftX,
            lowerLeftY,
            upperRightX,
            upperRightY,
            center,
            zoom,
            mapRect
          ) => {
            if (center) {
              dispatch({type: "SET_MAP_CENTER", location: center});
            }

            dispatch({
              type: "UPDATE_SEARCH_STATE",
              state: {
                searchInput: {
                  ...searchState.searchInput,
                  filter: {
                    ...(searchState.searchInput.filter || {}),
                    bounds: {
                      xmin: lowerLeftX,
                      ymin: lowerLeftY,
                      xmax: upperRightX,
                      ymax: upperRightY,
                    },
                  },
                  zoom,
                },
                mapCenter: center,
                mapRect,
              },
            });
          }}
          onSelection={selection =>
            dispatch({type: "SET_SELECTION", selection})
          }
          onHover={hover => dispatch({type: "SET_HOVER", hover})}
        />

        {hover && !isEmpty(hover.results) && (
          <div className={classes.hoverContainer} style={hoverContainerStyle}>
            <ul>
              {hover.results.map(result => {
                if (result.__typename && result.__typename === "MapCluster") {
                  const cluster = result as MapCluster;
                  return (
                    <li
                      key={`${cluster.__typename}:${cluster.location?.latitude}:${cluster.location?.longitude}`}>
                      <span>{cluster.count.toLocaleString()} camps</span>
                    </li>
                  );
                }

                const marker = result as SearchResult;
                return (
                  <li key={`${marker.type}:${marker.id}`}>
                    <span>{marker.name || `Unnamed #${marker.id}`}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {selection && selection.result && (
          <div
            className={classes.popoverContainer}
            style={popoverContainerStyle}>
            {selection.result.type === "Unit" && (
              <UnitPopover
                unit={{
                  id: parseInt(selection.result.id, 10),
                  slug: selection.result.slug || undefined,
                  name: selection.result.name,
                  __typename: selection.result.type,
                }}
                onClose={() =>
                  dispatch({type: "SET_SELECTION", selection: null})
                }
                {...popoverProps}
              />
            )}
            {selection.result.type === "Facility" && (
              <FacilityPopover
                facility={{
                  id: parseInt(selection.result.id, 10),
                  slug: selection.result.slug || undefined,
                  name: selection.result.name,
                  __typename: selection.result.type,
                }}
                onClose={() =>
                  dispatch({type: "SET_SELECTION", selection: null})
                }
                onWatchUpdated={() => refreshSearch()}
                filtersApplied={filtersApplied}
                {...popoverProps}
              />
            )}
            {selection.result.type === "Park" && (
              <ParkPopover
                park={{
                  id: parseInt(selection.result.id, 10),
                  slug: selection.result.slug || undefined,
                  name: selection.result.name,
                  __typename: selection.result.type,
                }}
                onClose={() =>
                  dispatch({type: "SET_SELECTION", selection: null})
                }
                {...popoverProps}
              />
            )}
          </div>
        )}

        {!isOnline && (
          <Alert
            className={classes.searchWarning}
            variant="filled"
            color="orange"
            radius="md"
            title="Network is offline"
            icon={<FontAwesomeIcon icon={faBan} beat />}></Alert>
        )}

        {isOnline && isEmpty(results) && !searchLoading && !searchError && (
          <Alert
            className={classes.searchWarning}
            variant="filled"
            color="orange"
            radius="md"
            title="No results found here"
            icon={<FontAwesomeIcon icon={faBan} beat />}></Alert>
        )}

        {isOnline && truncatedResults && (
          <Alert
            className={classes.searchWarning}
            variant="filled"
            color="orange"
            radius="md"
            title="Zoom in closer to see more results"
            icon={
              <FontAwesomeIcon icon={faMagnifyingGlassPlus} beat />
            }></Alert>
        )}

        {searchError && isOnline && (
          <div className={classes.error}>
            <Alert
              variant="filled"
              color="red"
              icon={
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  size="lg"
                  beatFade
                />
              }>
              {searchError.message}
            </Alert>
          </div>
        )}

        {showSearchLoader && <SearchLoader />}
      </div>

      <Drawer
        position="right"
        opened={showLayers}
        onClose={() => {
          dispatch({type: "SET_SHOW_LAYERS", value: false});
          if (typeof window !== "undefined") {
            // Window seems to resize on mobile, so this jumps to the top
            // so that the header doesn't disappear
            window.scrollTo(0, 0);
          }
        }}>
        <LayerPanel
          layers={layers}
          onChange={nextLayers => {
            dispatch({type: "SET_LAYERS", layers: nextLayers});
          }}
        />
      </Drawer>

      <Drawer
        position="right"
        opened={showFilters}
        onClose={() => {
          dispatch({type: "SET_SHOW_FILTERS", value: false});
          if (typeof window !== "undefined") {
            // Window seems to resize on mobile, so this jumps to the top
            // so that the header doesn't disappear
            window.scrollTo(0, 0);
          }
        }}>
        <FilterPanel
          className={classes.filterPanel}
          initialFilters={searchInput}
          searching={searchLoading}
          onChange={nextSearchInput => {
            dispatch({type: "SET_SHOW_FILTERS", value: false});
            dispatch({
              type: "UPDATE_SEARCH_INPUT",
              searchInput: nextSearchInput,
            });
          }}
        />
      </Drawer>

      <Tutorial
        id="map"
        run={runTutorial}
        steps={[
          {
            target: `.${classes.mapView}`,
            title: "Search results",
            content: (
              <div className={classes.tutorialStep}>
                <Text size="sm">
                  Here&apos;s the map with your matching search results. Click a
                  map marker to see what&apos;s there.
                </Text>
                <Space h="lg" />
                <img
                  src="/images/map/marker/Facility/matched.png"
                  height={36}
                  alt="Marker"
                />
              </div>
            ),
            placementBeacon: "top-start",
            disableBeacon: false,
            disableOverlay: true,
            disableScrolling: true,
          },
          {
            target: `.${classes.placePicker}`,
            title: "Search by Location",
            content: (
              <div className={classes.tutorialStep}>
                <Text size="sm">
                  If you have a specific place you&apos;re looking for, you can
                  quickly jump there in the map.
                </Text>
              </div>
            ),
            disableScrolling: true,
          },
          {
            target: `.${classes.filterButton}`,
            title: "Filter Results",
            content: (
              <div className={classes.tutorialStep}>
                <Text size="sm">
                  Use the filter to set your trip dates or refine the type of
                  camp site you&apos;re looking for.
                </Text>
                <Space h="xl" />
                <Text size="sm">
                  Filters affect how your search results appear:
                </Text>
                <Space h="sm" />
                <Group>
                  <img
                    src="/images/map/marker/Facility/matched.png"
                    height={32}
                    alt="Matched marker"
                  />
                  <Text size="xs">
                    Camp that <strong>matches</strong> your filter
                  </Text>
                </Group>
                <Group>
                  <img
                    src="/images/map/marker/Facility/unmatched.png"
                    height={32}
                    alt="Unmatched marker"
                  />
                  <Text size="xs">
                    Camp that <strong>doesn&apos;t match</strong> your filter
                  </Text>
                </Group>
                <Space h="xl" />
                <Text size="xs">
                  You can also hide irrelevant results from the map.
                </Text>
                <Space h="md" />
                <Text size="xs">
                  For more details, see <HelpLink slug="filters" />.
                </Text>
              </div>
            ),
            disableScrolling: true,
          },
          {
            target: `.${classes.layersButton}`,
            title: "Enable map layers",
            content: (
              <div className={classes.tutorialStep}>
                <Text size="sm">
                  Enable extra map layers to help you find a better camp site,
                  such as <strong>free camping</strong> area boundaries or{" "}
                  <strong>snow cover</strong>.
                </Text>
              </div>
            ),
            disableScrolling: true,
          },
          {
            target: `.${classes.locationButton}`,
            title: "My Location",
            content: (
              <div className={classes.tutorialStep}>
                <Text size="sm">
                  Center the map on your detected location or even live track
                  your location as you move around.
                </Text>
              </div>
            ),
          },
          {
            target: `.${classes.mapView} .ol-zoom`,
            title: "Zoom",
            content: (
              <div className={classes.tutorialStep}>
                <Text size="sm">
                  Zoom the map with these buttons. You can also pinch or scroll
                  your mouse to do this more quickly.
                </Text>
              </div>
            ),
            placement: "left",
            disableScrolling: true,
          },
        ]}
      />
    </div>
  );
}
